/**
 * One row in the battle action history drawer: actor avatar + verbatim text + entity thumbnails (no duplicate names under thumbs).
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getAvatarImage, getOpponentAvatarImage } from '../avatars';
import { buildBattleActionHistoryDrawerLines } from '../utils/battleActionHistoryI18n';
import LogTextWithUnitNames from './LogTextWithUnitNames';
import { TooltipOrderCard, TooltipGeneralAvatar } from './BattleActionLogCards';
import {
  BattleActionLogResourceDeltaArrow,
  getBattleActionLogResourceDeltaLinesForDrawer,
  getBattleActionLogAttackThumbHealthLines
} from './battleActionLogResourceDeltas';

function avatarSrcForPlayer(playerId, gameState, userId, opponentId) {
  if (!gameState || playerId == null) return '/images/default.jpg';
  const isAutobattlerMode = gameState.isAutobattler || gameState.devAutobattlerMode;
  const isSpectatorMode = gameState.spectatorUserId && gameState.spectatorUserId === userId;
  const shouldUsePlayerAvatars = isAutobattlerMode || isSpectatorMode;
  if (String(playerId) === String(opponentId)) {
    const opponentAvatars = shouldUsePlayerAvatars
      ? (gameState.playerAvatars?.[playerId] || {})
      : (gameState.opponentAvatars?.[userId] || {});
    const opponentState = gameState.state?.[playerId];
    const opponentGeneral = opponentAvatars.general || opponentState?.selectedGeneral || gameState.opponentGeneral || '';
    return getOpponentAvatarImage(opponentGeneral);
  }
  const playerAvatars = gameState.playerAvatars?.[playerId] || {};
  const playerState = gameState.state?.[playerId];
  const playerGeneral = playerAvatars.general || playerState?.selectedGeneral || '';
  return getAvatarImage(playerGeneral);
}

function displayNameForPlayer(pid, userId, opponentId, currentUsername, opponentUsername, t) {
  if (pid == null) return '';
  if (String(pid) === String(userId)) return currentUsername || String(pid);
  if (opponentId != null && String(pid) === String(opponentId)) return opponentUsername || String(pid);
  if (pid === 'bot' || pid === 'campaign_bot') {
    return t('battleUI.actionHistory.botPlayerName', { defaultValue: 'Bot' });
  }
  return String(pid);
}

function ThumbnailEmbeddedResourceDelta({ delta, kind }) {
  if (
    !delta ||
    typeof delta.before !== 'number' ||
    typeof delta.after !== 'number' ||
    delta.before === delta.after
  ) {
    return null;
  }
  return (
    <div
      className={`battle-action-log-drawer-resource-delta-line battle-action-log-drawer-resource-delta-line--${kind} battle-action-log-drawer-thumb-embedded-delta`}
      aria-label={`${delta.before} → ${delta.after}`}
    >
      <span className="battle-action-log-drawer-resource-delta-num">{delta.before}</span>
      <BattleActionLogResourceDeltaArrow />
      <span className="battle-action-log-drawer-resource-delta-num">{delta.after}</span>
    </div>
  );
}

export function ThumbnailWithLabel({
  orderLike,
  gameState,
  unitRegistryRef,
  zoneName,
  showSkull,
  healthDelta,
  intelDelta,
  generalDelta,
  onMouseEnter,
  onMouseLeave,
  compactHeaderPreview = false,
}) {
  if (!orderLike || orderLike.id == null && !orderLike.instanceId) return null;
  const showHp =
    !compactHeaderPreview
    && healthDelta
    && typeof healthDelta.before === 'number'
    && typeof healthDelta.after === 'number'
    && healthDelta.before !== healthDelta.after;
  return (
    <div
      className={[
        'battle-action-log-drawer-thumb-col',
        compactHeaderPreview ? 'battle-action-log-drawer-thumb-col--header-compact' : '',
      ].filter(Boolean).join(' ')}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="battle-action-log-drawer-thumb-wrap">
        <TooltipOrderCard order={orderLike} gameState={gameState} unitRegistryRef={unitRegistryRef} showSkull={showSkull} zoneName={zoneName} />
      </div>
      {showHp ? (
        <div
          className="battle-action-log-drawer-thumb-hp-delta"
          aria-label={`${healthDelta.before} → ${healthDelta.after}`}
        >
          <span className="battle-action-log-drawer-resource-delta-num">{healthDelta.before}</span>
          <BattleActionLogResourceDeltaArrow />
          <span className="battle-action-log-drawer-resource-delta-num">{healthDelta.after}</span>
        </div>
      ) : null}
      {!compactHeaderPreview ? (
        <>
      <ThumbnailEmbeddedResourceDelta delta={intelDelta} kind="intel" />
      <ThumbnailEmbeddedResourceDelta delta={generalDelta} kind="general" />
        </>
      ) : null}
    </div>
  );
}

function PlayerActorCell({
  playerId,
  gameState,
  userId,
  opponentId,
  currentUsername,
  opponentUsername,
  onMouseEnter,
  onMouseLeave
}) {
  const { t } = useTranslation();
  const src = avatarSrcForPlayer(playerId, gameState, userId, opponentId);
  const name = displayNameForPlayer(playerId, userId, opponentId, currentUsername, opponentUsername, t);
  return (
    <div
      className="battle-action-log-drawer-actor"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <img src={src} alt="" className="battle-action-log-drawer-actor-img" />
      <div className="battle-action-log-drawer-actor-name">{name}</div>
    </div>
  );
}

/** @param {{ orderLike: object, gameState: object, userId: string, onExpand: function, onExpandClear: function }} p */
function attachExpandHandlers(orderLike, onExpand, onExpandClear) {
  if (!orderLike || !onExpand) return {};
  return {
    onMouseEnter: () => {
      onExpand(orderLike);
    },
    onMouseLeave: () => {
      if (onExpandClear) onExpandClear();
    }
  };
}

function VerbatimMiddle({ entry, t, nameCtx, baseClass, gameState }) {
  const lines = buildBattleActionHistoryDrawerLines(entry, t, nameCtx);
  const multiline = lines.length > 1;
  return (
    <div className={`${baseClass}${multiline ? ' battle-action-log-drawer-middle--multiline' : ''}`}>
      {lines.map((line, i) => (
        <div key={i}>
          <LogTextWithUnitNames payload={entry} line={line} gameState={gameState} />
        </div>
      ))}
    </div>
  );
}

export default function BattleActionLogRow({
  entry,
  gameState,
  userId,
  opponentId,
  currentUsername,
  opponentUsername,
  isMobile,
  onExpandOrder,
  onExpandOrderClear,
  unitRegistryRef = null
}) {
  const { t } = useTranslation();
  const primary = entry.unit || entry.order || entry.attacker || entry.drone || entry.weapon || entry.strategem || entry.source;
  const pid = entry.playerId;

  const nameCtx = useMemo(
    () => ({ userId, opponentId, currentUsername, opponentUsername }),
    [userId, opponentId, currentUsername, opponentUsername]
  );

  const ex = !isMobile && onExpandOrder
    ? (orderLike) => attachExpandHandlers(orderLike, onExpandOrder, onExpandOrderClear)
    : () => ({});

  const middleClass = 'battle-action-log-drawer-middle';

  const renderActor = () =>
    pid != null ? (
      <PlayerActorCell
        playerId={pid}
        gameState={gameState}
        userId={userId}
        opponentId={opponentId}
        currentUsername={currentUsername}
        opponentUsername={opponentUsername}
      />
    ) : (
      <div className="battle-action-log-drawer-actor battle-action-log-drawer-actor--empty" aria-hidden />
    );

  const attackThumbHealthLines = useMemo(() => getBattleActionLogAttackThumbHealthLines(entry), [entry]);
  const { globalLines: resourceDeltaLines, attackColumns } = useMemo(
    () => getBattleActionLogResourceDeltaLinesForDrawer(entry),
    [entry]
  );

  const renderThumbnails = () => {
    switch (entry.type) {
      case 'order-received-hand':
        if (pid == null || String(pid) !== String(userId)) return null;
        return primary ? (
          <ThumbnailWithLabel orderLike={primary} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(primary)} />
        ) : null;
      case 'turn-passed':
      case 'turn-started':
        return null;
      case 'barrier-lost': {
        const lostPid = entry.playerId;
        return lostPid != null ? (
          <div className="battle-action-log-drawer-thumb-col">
            <div className="battle-action-log-drawer-general-wrap">
              <TooltipGeneralAvatar
                targetPlayerId={lostPid}
                gameState={gameState}
                userId={userId}
                opponentId={opponentId}
              />
            </div>
          </div>
        ) : null;
      }
      case 'unit-deployed':
        return primary ? (
          <ThumbnailWithLabel orderLike={primary} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(primary)} />
        ) : null;
      case 'weapon-equipped':
        return (
          <>
            <ThumbnailWithLabel
              orderLike={entry.weapon || primary}
              gameState={gameState} unitRegistryRef={unitRegistryRef}
              zoneName="tooltip"
              {...ex(entry.weapon || primary)}
            />
            {entry.target ? (
              <ThumbnailWithLabel orderLike={entry.target} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(entry.target)} />
            ) : null}
          </>
        );
      case 'weapon-stat-bonus':
        return (
          <>
            {entry.weapon ? (
              <ThumbnailWithLabel orderLike={entry.weapon} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(entry.weapon)} />
            ) : null}
            {entry.target ? (
              <ThumbnailWithLabel orderLike={entry.target} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(entry.target)} />
            ) : null}
          </>
        );
      case 'drone-deployed':
        return (
          <ThumbnailWithLabel
            orderLike={entry.drone || primary}
            gameState={gameState} unitRegistryRef={unitRegistryRef}
            zoneName="tooltip"
            {...ex(entry.drone || primary)}
          />
        );
      case 'drone-charge-used':
        return (
          <>
            <ThumbnailWithLabel
              orderLike={entry.drone || primary}
              gameState={gameState} unitRegistryRef={unitRegistryRef}
              zoneName="tooltip"
              {...ex(entry.drone || primary)}
            />
            {entry.target ? (
              <ThumbnailWithLabel orderLike={entry.target} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(entry.target)} />
            ) : null}
          </>
        );
      case 'drone-aura-applied':
        return entry.drone ? (
          <ThumbnailWithLabel orderLike={entry.drone} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(entry.drone)} />
        ) : null;
      case 'drone-exhausted-deploy':
        return (
          <>
            {entry.drone ? (
              <ThumbnailWithLabel orderLike={entry.drone} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(entry.drone)} />
            ) : null}
            {entry.spawnedUnit ? (
              <ThumbnailWithLabel
                orderLike={entry.spawnedUnit}
                gameState={gameState} unitRegistryRef={unitRegistryRef}
                zoneName="tooltip"
                {...ex(entry.spawnedUnit)}
              />
            ) : null}
          </>
        );
      case 'ability-relay-grant':
        return (
          <>
            {entry.drone ? (
              <ThumbnailWithLabel orderLike={entry.drone} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(entry.drone)} />
            ) : null}
            {entry.target ? (
              <ThumbnailWithLabel orderLike={entry.target} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(entry.target)} />
            ) : null}
          </>
        );
      case 'unit-attacked':
        return (
          <>
            <ThumbnailWithLabel
              orderLike={entry.attacker || primary}
              gameState={gameState} unitRegistryRef={unitRegistryRef}
              zoneName="tooltip"
              showSkull={entry.attackerDestroyed}
              healthDelta={attackThumbHealthLines[0]}
              intelDelta={attackColumns?.attackerIntel}
              generalDelta={attackColumns?.attackerGeneral}
              {...ex(entry.attacker || primary)}
            />
            {entry.target ? (
              <ThumbnailWithLabel
                orderLike={entry.target}
                gameState={gameState} unitRegistryRef={unitRegistryRef}
                zoneName="tooltip"
                showSkull={entry.targetDestroyed}
                healthDelta={attackThumbHealthLines[1]}
                intelDelta={attackColumns?.targetIntel}
                generalDelta={attackColumns?.targetGeneral}
                {...ex(entry.target)}
              />
            ) : null}
          </>
        );
      case 'unit-redeem-damage':
      case 'unit-special-effect-damage':
        return (
          <>
            <ThumbnailWithLabel
              orderLike={entry.unit || primary}
              gameState={gameState} unitRegistryRef={unitRegistryRef}
              zoneName="tooltip"
              showSkull={false}
              {...ex(entry.unit || primary)}
            />
            {entry.target ? (
              <ThumbnailWithLabel
                orderLike={entry.target}
                gameState={gameState} unitRegistryRef={unitRegistryRef}
                zoneName="tooltip"
                showSkull={entry.targetDestroyed}
                {...ex(entry.target)}
              />
            ) : null}
          </>
        );
      case 'unit-attacked-general': {
        const tgtPid = entry.targetPlayerId ?? entry.outcome?.targetPlayerId;
        return (
          <>
            <ThumbnailWithLabel
              orderLike={entry.attacker || primary}
              gameState={gameState} unitRegistryRef={unitRegistryRef}
              zoneName="tooltip"
              showSkull={entry.attackerDestroyed}
              {...ex(entry.attacker || primary)}
            />
            {tgtPid != null ? (
              <div className="battle-action-log-drawer-thumb-col">
                <div className="battle-action-log-drawer-general-wrap">
                  <TooltipGeneralAvatar
                    targetPlayerId={tgtPid}
                    gameState={gameState}
                    userId={userId}
                    opponentId={opponentId}
                  />
                </div>
              </div>
            ) : null}
          </>
        );
      }
      case 'penetrate-excess-general':
      case 'attack-effect-general-damage': {
        const genPid = entry.generalPlayerId;
        const generalDelta =
          entry.affectedGeneralHealth &&
          typeof entry.affectedGeneralHealth.before === 'number' &&
          typeof entry.affectedGeneralHealth.after === 'number' &&
          entry.affectedGeneralHealth.before !== entry.affectedGeneralHealth.after
            ? { before: entry.affectedGeneralHealth.before, after: entry.affectedGeneralHealth.after }
            : null;
        return (
          <>
            {entry.attacker ? (
              <ThumbnailWithLabel orderLike={entry.attacker} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(entry.attacker)} />
            ) : null}
            {genPid != null ? (
              <div className="battle-action-log-drawer-thumb-col">
                <div className="battle-action-log-drawer-general-wrap">
                  <TooltipGeneralAvatar
                    targetPlayerId={genPid}
                    gameState={gameState}
                    userId={userId}
                    opponentId={opponentId}
                  />
                  {generalDelta ? (
                    <ThumbnailEmbeddedResourceDelta delta={generalDelta} kind="general" />
                  ) : null}
                </div>
              </div>
            ) : null}
          </>
        );
      }
      case 'fell-intel':
        return (
          <>
            {entry.attacker ? (
              <ThumbnailWithLabel orderLike={entry.attacker} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(entry.attacker)} />
            ) : null}
            {entry.target ? (
              <ThumbnailWithLabel orderLike={entry.target} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(entry.target)} />
            ) : null}
          </>
        );
      case 'fell-summon':
        return (
          <>
            {entry.attacker ? (
              <ThumbnailWithLabel orderLike={entry.attacker} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(entry.attacker)} />
            ) : null}
            {entry.summonedUnit ? (
              <ThumbnailWithLabel
                orderLike={entry.summonedUnit}
                gameState={gameState} unitRegistryRef={unitRegistryRef}
                zoneName="tooltip"
                {...ex(entry.summonedUnit)}
              />
            ) : null}
          </>
        );
      case 'strategem-hive-surge-buff':
        return (
          <>
            {primary ? (
              <ThumbnailWithLabel orderLike={primary} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(primary)} />
            ) : null}
            {(entry.targets || []).map((u, i) => (
              <ThumbnailWithLabel
                key={u?.instanceId ?? i}
                orderLike={u}
                gameState={gameState} unitRegistryRef={unitRegistryRef}
                zoneName="tooltip"
                {...ex(u)}
              />
            ))}
          </>
        );
      case 'strategem-damaged-general':
      case 'strategem-healed-general': {
        const tgtPid = entry.targetPlayerId;
        return (
          <>
            {primary ? (
              <ThumbnailWithLabel orderLike={primary} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(primary)} />
            ) : null}
            {tgtPid != null ? (
              <div className="battle-action-log-drawer-thumb-col">
                <div className="battle-action-log-drawer-general-wrap">
                  <TooltipGeneralAvatar
                    targetPlayerId={tgtPid}
                    gameState={gameState}
                    userId={userId}
                    opponentId={opponentId}
                  />
                </div>
              </div>
            ) : null}
          </>
        );
      }
      default:
        return primary ? (
          <ThumbnailWithLabel orderLike={primary} gameState={gameState} unitRegistryRef={unitRegistryRef} zoneName="tooltip" {...ex(primary)} />
        ) : null;
    }
  };

  return (
    <div className="battle-action-log-drawer-row" role="listitem">
      {renderActor()}
      <VerbatimMiddle entry={entry} t={t} nameCtx={nameCtx} baseClass={middleClass} gameState={gameState} />
      <div className="battle-action-log-drawer-right-col">
        <div className="battle-action-log-drawer-thumbs-row">{renderThumbnails()}</div>
        {resourceDeltaLines.length > 0 ? (
          <div className="battle-action-log-drawer-resource-deltas" aria-label={t('battleUI.actionHistory.resourceDeltas', { defaultValue: 'Resource changes' })}>
            {resourceDeltaLines.map((item, i) => (
              <div
                key={i}
                className={`battle-action-log-drawer-resource-delta-line battle-action-log-drawer-resource-delta-line--${item.kind}`}
              >
                <span className="battle-action-log-drawer-resource-delta-num">{item.before}</span>
                <BattleActionLogResourceDeltaArrow />
                <span className="battle-action-log-drawer-resource-delta-num">{item.after}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
